/**
 * DMarket API Service
 * Integration with DMarket trading/marketplace APIs via backend proxy
 */

const BACKEND_BASE_URL = '/api/dmarket';
const AFFILIATE_LINK = 'https://dmarket.com?ref=krTE9HvlDC';

/**
 * Make request to backend DMarket proxy
 */
const dmarketRequest = async (endpoint, options = {}) => {
  const url = `${BACKEND_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`DMarket proxy error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'API request failed');
    }

    return data.data;
  } catch (error) {
    console.error('DMarket API request failed:', error);
    throw error;
  }
};

/**
 * Get available games to find CS2 game ID
 */
export const getGames = async () => {
  return dmarketRequest('/games');
};

/**
 * Search for items by title
 */
export const searchItems = async (title, limit = 10) => {
  const params = new URLSearchParams({
    title: title,
    limit: limit.toString()
  });

  return dmarketRequest(`/search?${params}`);
};

/**
 * Search available items (quick search)
 */
export const searchAvailableItems = async (title, limit = 10) => {
  const params = new URLSearchParams({
    title: title,
    limit: limit.toString()
  });

  return dmarketRequest(`/search/available?${params}`);
};

/**
 * Get sell offers for a specific class ID
 */
export const getSellOffers = async (classId, options = {}) => {
  const {
    currency = 'USD',
    orderBy = 'price',
    orderDir = 'asc',
    limit = 20,
    tradability = 'all'
  } = options;

  const params = new URLSearchParams({
    currency,
    orderBy,
    orderDir,
    limit: limit.toString(),
    tradability
  });

  return dmarketRequest(`/offers/${classId}?${params}`);
};

/**
 * Get market depth (price range) for an item
 */
export const getMarketDepth = async (classId, currency = 'USD') => {
  const params = new URLSearchParams({
    classId,
    currency
  });

  return dmarketRequest(`/market-depth?${params}`);
};

/**
 * Build buy link for DMarket with affiliate (fallback function)
 * Note: Backend now generates proper product page URLs
 */
export const buildBuyLink = (classId, offerId = null) => {
  // Fallback to general CS:GO skins page with affiliate
  return `${AFFILIATE_LINK}/csgo-skins`;
};

/**
 * Format skin name for DMarket search
 * Convert from "Weapon | Skin" to "Weapon Skin" format
 */
export const formatSkinNameForSearch = (weaponName, skinName) => {
  // Handle special cases and formatting
  let formattedWeapon = weaponName;
  let formattedSkin = skinName;
  
  // Convert weapon names to DMarket format
  const weaponMap = {
    'AK-47': 'AK-47',
    'M4A4': 'M4A4',
    'M4A1-S': 'M4A1-S',
    'AWP': 'AWP',
    'Glock-18': 'Glock-18',
    'USP-S': 'USP-S',
    'Knife': 'Knife'
  };

  formattedWeapon = weaponMap[weaponName] || weaponName;
  
  // Remove special characters and clean up skin name
  formattedSkin = skinName.replace(/[^\w\s-]/g, '').trim();
  
  return `${formattedWeapon} ${formattedSkin}`;
};

/**
 * Get skin pricing data (main function for skin detail page)
 */
export const getSkinPricing = async (weaponName, skinName) => {
  try {
    const params = new URLSearchParams({
      weaponName,
      skinName
    });

    const response = await fetch(`${BACKEND_BASE_URL}/skin-pricing?${params}`);
    
    if (!response.ok) {
      throw new Error(`Backend proxy error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      return {
        found: false,
        message: data.message || 'Failed to fetch pricing data'
      };
    }

    return data;

  } catch (error) {
    console.error('Error fetching skin pricing:', error);
    return {
      found: false,
      error: error.message,
      message: 'Error fetching pricing data'
    };
  }
};

const dmarketService = {
  getGames,
  searchItems,
  searchAvailableItems,
  getSellOffers,
  getMarketDepth,
  buildBuyLink,
  formatSkinNameForSearch,
  getSkinPricing
};

export default dmarketService;
